// /tickets/canvas — 3-pane board: left epic nav, center grouped board,
// right filters + selected-ticket detail.
//
// Grouping is stored in s.root.canvasGroupBy (default 'story').
// Active epic filter in s.root.canvasEpicFilter (null = all).
// Selected ticket in s.root.selectedTicket (opens right-pane detail).
// Drag-to-bucket: pointer events delegated at board level, not per-card.
// URL state: ?groupBy=story&epic=<key>
//
// Drag mutation map:
//   status   → PATCH state
//   priority → PATCH priority
//   assignee → PATCH assignee_email
//   epic     → PATCH parent_ticket_id (epic row id)
//   story    → PATCH parent_ticket_id (story row id)

import { groupTickets, patchTicketField } from '../../functions/tickets/fetchCanvasTriage.js'
import { TICKET_STATE_COLOR, PRIORITY_COLOR } from '../../functions/tickets/ticketUtils.js'

// ── Helpers ──────────────────────────────────────────────────────────────────

const _groupOptions = [
  { key: 'story',    label: 'Story' },
  { key: 'epic',     label: 'Epic' },
  { key: 'status',   label: 'Status' },
  { key: 'priority', label: 'Priority' },
  { key: 'assignee', label: 'Assignee' },
  { key: 'none',     label: 'None' }
]

const _getGroupBy = (root) => root?.canvasGroupBy || 'story'

const _getActiveEpic = (root) => root?.canvasEpicFilter || null

// Filter + re-group canvasAllTickets based on current root settings.
const _computeGroups = (root) => {
  const all = root?.canvasAllTickets || []
  const epicFilter = _getActiveEpic(root)
  const stateFilter = root?.canvasStateFilter || null
  const priorityFilter = root?.canvasPriorityFilter || null
  const groupBy = _getGroupBy(root)

  let filtered = all
  if (epicFilter) filtered = filtered.filter(t => t.parentEpicKey === epicFilter)
  if (stateFilter) filtered = filtered.filter(t => t.state === stateFilter)
  if (priorityFilter) filtered = filtered.filter(t => t.priority === priorityFilter)

  return groupTickets(filtered, groupBy)
}

// Persist ?groupBy= + ?epic= to the URL without a page reload.
const _syncUrl = (root) => {
  if (typeof window === 'undefined') return
  try {
    const u = new URL(window.location)
    const g = _getGroupBy(root)
    if (g && g !== 'story') u.searchParams.set('groupBy', g)
    else u.searchParams.delete('groupBy')
    const e = _getActiveEpic(root)
    if (e) u.searchParams.set('epic', e)
    else u.searchParams.delete('epic')
    window.history.replaceState(null, '', u)
  } catch (_) {}
}

// Board-level drag handler — attached once to the center pane container.
// Avoids per-card listener attachment (prevents #4382-style tree cascade).
let _rafId = null
const _onBoardPointerMove = (e) => {
  if (_rafId) cancelAnimationFrame(_rafId)
  _rafId = requestAnimationFrame(() => {
    _rafId = null
    // Highlight drop targets via CSS data attribute
    document.querySelectorAll('[data-drop-group]').forEach(el => {
      const rect = el.getBoundingClientRect()
      const over = e.clientX >= rect.left && e.clientX <= rect.right &&
                   e.clientY >= rect.top && e.clientY <= rect.bottom
      el.dataset.dragOver = over ? 'true' : ''
    })
  })
}

// Toast helper — post to root.canvasToast for a 2.5s flash.
const _toast = (root, msg, ok = true) => {
  if (!root || typeof root.update !== 'function') return
  root.update({ canvasToast: { msg, ok, ts: Date.now() } })
  setTimeout(() => root.update({ canvasToast: null }), 2500)
}

// Mutation key for drop target (what field + value to PATCH).
const _dropMutation = (groupBy, groupKey, root) => {
  if (groupBy === 'status')   return { field: 'state', value: groupKey }
  if (groupBy === 'priority') return { field: 'priority', value: groupKey === '_none' ? null : groupKey }
  if (groupBy === 'assignee') return { field: 'assignee_email', value: groupKey === '_unassigned' ? null : groupKey }
  if (groupBy === 'epic') {
    const epics = root?.canvasEpics || []
    const ep = epics.find(e => e.epicKey === groupKey)
    return { field: 'parent_ticket_id', value: ep?.id || null }
  }
  if (groupBy === 'story') {
    const stories = root?.canvasStories || []
    const st = stories.find(s => String(s.id) === groupKey)
    return { field: 'parent_ticket_id', value: st?.id || null }
  }
  return null
}

// Accent colour for a group header based on groupBy + groupKey.
const _groupAccent = (groupBy, groupKey) => {
  if (groupBy === 'status')   return TICKET_STATE_COLOR[groupKey] || 'caption'
  if (groupBy === 'priority') return PRIORITY_COLOR[groupKey] || 'caption'
  if (groupBy === 'epic')     return 'cyan'
  if (groupBy === 'story')    return 'blue'
  return 'caption'
}

// ── Page shell ────────────────────────────────────────────────────────────────

export const TicketsCanvasMain = {
  extends: 'AppMain',
  padding: '0',

  Header: { extends: 'TicketsHeader' },

  Body: {
    overflow: 'auto',
    flow: 'y',

    // ── Top bar ─────────────────────────────────────────────────────────────
    TopBar: {
      flow: 'x',
      align: 'center left',
      gap: 'A',
      padding: 'Z A',
      borderBottomWidth: 'V',
      borderBottomStyle: 'solid',
      borderBottomColor: 'line',
      flexWrap: 'wrap',

      PageTitle: {
        tag: 'h1',
        margin: '0',
        fontSize: 'B',
        fontWeight: '700',
        letterSpacing: '-.02em',
        color: 'title',
        text: 'Canvas'
      },

      Sep: {
        tag: 'span',
        color: 'caption',
        opacity: '.3',
        fontSize: 'B',
        text: '/'
      },

      // Breadcrumb epic chip (shown when epic filter is active)
      EpicBreadcrumb: {
        hide: (el, s) => !_getActiveEpic(s.root),
        flow: 'x',
        gap: 'V',
        align: 'center left',
        EpicLabel: {
          tag: 'span',
          padding: 'V Z',
          round: 'Y2',
          fontSize: 'Y2',
          fontWeight: '700',
          background: 'cyan.15',
          color: 'cyan',
          text: (el, s) => {
            const e = _getActiveEpic(s.root)
            const epics = s.root?.canvasEpics || []
            const ep = epics.find(x => x.epicKey === e)
            return ep?.title || (e || '').toUpperCase()
          }
        },
        ClearEpic: {
          tag: 'button',
          type: 'button',
          padding: 'V Z',
          round: 'Y2',
          border: 'none',
          cursor: 'pointer',
          background: 'line',
          color: 'caption',
          fontSize: 'Y2',
          text: '×',
          title: 'Clear epic filter',
          onClick: (e, el, s) => {
            s.root.update({ canvasEpicFilter: null })
            _syncUrl(s.root)
          }
        }
      },

      Spacer: { flex: '1' },

      // Group-by selector
      GroupByLabel: {
        tag: 'label',
        fontSize: 'Y2',
        color: 'caption',
        fontWeight: '600',
        for: 'canvas-groupby',
        text: 'Group by'
      },

      GroupBySelect: {
        tag: 'select',
        id: 'canvas-groupby',
        fontSize: 'Y2',
        fontWeight: '600',
        padding: 'V Z',
        round: 'Y2',
        borderWidth: 'V',
        borderStyle: 'solid',
        borderColor: 'line',
        background: 'overlay',
        color: 'title',
        cursor: 'pointer',
        onRender: (el, s) => {
          if (!el.node) return
          const val = _getGroupBy(s.root)
          el.node.innerHTML = _groupOptions.map(o =>
            `<option value="${o.key}"${o.key === val ? ' selected' : ''}>${o.label}</option>`
          ).join('')
        },
        onChange: (e, el, s) => {
          const val = el.node?.value || 'story'
          s.root.update({ canvasGroupBy: val })
          _syncUrl(s.root)
        }
      },

      // State filter pill strip
      StateFilters: {
        flow: 'x',
        gap: 'V',
        align: 'center left',
        flexWrap: 'wrap',

        childExtends: 'CanvasFilterChip',
        childrenAs: 'state',
        children: (el, s) => {
          const active = s.root?.canvasStateFilter || null
          return [
            { key: null, label: 'All', filterKey: 'canvasStateFilter', active: !active },
            { key: 'in_progress', label: 'In Progress', filterKey: 'canvasStateFilter', active: active === 'in_progress' },
            { key: 'not_started', label: 'Not Started', filterKey: 'canvasStateFilter', active: active === 'not_started' },
            { key: 'done', label: 'Done', filterKey: 'canvasStateFilter', active: active === 'done' }
          ]
        }
      },

      // Count badge
      CountBadge: {
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        text: (el, s) => {
          const groups = _computeGroups(s.root)
          const total = groups.reduce((a, g) => a + g.tickets.length, 0)
          return `${total} ticket${total === 1 ? '' : 's'}`
        }
      }
    },

    // ── 3-pane layout ────────────────────────────────────────────────────────
    Panes: {
      flow: 'x',
      flex: '1',
      overflow: 'hidden',
      minHeight: '0',
      alignItems: 'stretch',

      // ── LEFT: Epic navigation rail ─────────────────────────────────────────
      EpicRail: {
        width: '220px',
        minWidth: '180px',
        maxWidth: '260px',
        flexShrink: '0',
        flow: 'y',
        borderRightWidth: 'V',
        borderRightStyle: 'solid',
        borderRightColor: 'line',
        overflow: 'auto',

        RailHeader: {
          padding: 'A A V A',
          flow: 'x',
          align: 'baseline space-between',
          borderBottomWidth: 'V',
          borderBottomStyle: 'solid',
          borderBottomColor: 'line',

          RailTitle: {
            tag: 'h2',
            margin: '0',
            fontSize: 'Y1',
            fontWeight: '700',
            letterSpacing: '.1em',
            color: 'caption',
            text: 'EPICS'
          },
          EpicCount: {
            tag: 'span',
            fontSize: 'Y2',
            color: 'caption',
            text: (el, s) => `${(s.root?.canvasEpics || []).length}`
          }
        },

        AllEpicBtn: {
          flow: 'x',
          gap: 'V',
          align: 'center left',
          padding: 'Z A',
          cursor: 'pointer',
          transition: 'background 100ms ease',
          borderLeftWidth: '3px',
          borderLeftStyle: 'solid',
          borderLeftColor: (el, s) => !_getActiveEpic(s.root) ? 'cyan' : 'transparent',
          background: (el, s) => !_getActiveEpic(s.root) ? 'cyan.06' : 'transparent',
          ':hover': { background: 'overlay' },
          onClick: (e, el, s) => {
            s.root.update({ canvasEpicFilter: null })
            _syncUrl(s.root)
          },
          AllLabel: {
            tag: 'span',
            fontSize: 'Y2',
            fontWeight: (el, s) => !_getActiveEpic(s.root) ? '700' : '400',
            color: (el, s) => !_getActiveEpic(s.root) ? 'cyan' : 'title',
            text: 'All Epics'
          }
        },

        EpicList: {
          flow: 'y',
          padding: 'V 0',

          childExtends: 'CanvasEpicRailItem',
          childrenAs: 'state',
          children: (el, s) => s.root?.canvasEpics || []
        }
      },

      // ── CENTER: Board ───────────────────────────────────────────────────────
      BoardPane: {
        flex: '1',
        flow: 'y',
        overflow: 'auto',
        minWidth: '0',

        // Loading state
        Loading: {
          hide: (el, s) => s.root?.canvasAllTickets != null,
          flow: 'y',
          align: 'center center',
          gap: 'A',
          padding: 'D',
          flex: '1',

          Spinner: {
            boxSize: 'C',
            round: '50%',
            borderWidth: '2px',
            borderStyle: 'solid',
            borderColor: 'caption.2',
            borderTopColor: 'cyan',
            animation: 'canvas-spin 0.9s linear infinite',
            '@keyframes canvas-spin': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' }
            }
          },
          Label: {
            tag: 'p',
            margin: '0',
            fontSize: 'A',
            color: 'caption',
            text: 'Loading board…'
          }
        },

        // Empty state per current filter
        BoardEmpty: {
          hide: (el, s) => {
            const all = s.root?.canvasAllTickets
            if (all == null) return true
            return _computeGroups(s.root).some(g => g.tickets.length > 0)
          },
          flow: 'y',
          align: 'center center',
          gap: 'A',
          padding: 'D',
          flex: '1',
          textAlign: 'center',

          Icon: { extends: 'Icon', icon: 'magicstar', boxSize: 'D', color: 'caption', opacity: '.4' },
          Title: {
            tag: 'h3',
            margin: '0',
            fontSize: 'B',
            fontWeight: '600',
            color: 'title',
            text: 'No tickets to show'
          },
          Lede: {
            tag: 'p',
            margin: '0',
            fontSize: 'A',
            color: 'caption',
            lineHeight: '1.5',
            text: 'Try removing filters or choosing a different grouping.'
          }
        },

        // Multi-select action bar (bottom of center pane)
        MultiSelectBar: {
          hide: (el, s) => !(s.root?.canvasSelected?.length > 0),
          position: 'sticky',
          bottom: '0',
          flow: 'x',
          align: 'center space-between',
          gap: 'A',
          padding: 'Z A',
          background: 'overlay',
          borderTopWidth: 'V',
          borderTopStyle: 'solid',
          borderTopColor: 'line',
          backdropFilter: 'blur(12px)',
          zIndex: '10',

          SelCount: {
            tag: 'span',
            fontSize: 'Y1',
            fontWeight: '700',
            color: 'title',
            text: (el, s) => {
              const n = (s.root?.canvasSelected || []).length
              return `${n} ticket${n === 1 ? '' : 's'} selected`
            }
          },

          Actions: {
            flow: 'x',
            gap: 'V',

            ClearSel: {
              tag: 'button',
              type: 'button',
              padding: 'V Z',
              round: 'Y2',
              border: 'none',
              cursor: 'pointer',
              background: 'line',
              color: 'caption',
              fontSize: 'Y2',
              fontWeight: '600',
              text: 'Clear',
              onClick: (e, el, s) => { s.root.update({ canvasSelected: [] }) }
            }
          }
        },

        // Board groups container — pointer events for drag delegated here.
        BoardGroups: {
          flow: 'y',
          gap: 'B',
          padding: 'B A D A',

          // Attach board-level pointer listener once.
          onInit: (el) => {
            if (!el.node) return
            el.node.addEventListener('pointermove', _onBoardPointerMove, { passive: true })
          },
          onRemove: (el) => {
            if (!el.node) return
            el.node.removeEventListener('pointermove', _onBoardPointerMove)
          },

          childExtends: 'CanvasBoardGroup',
          childrenAs: 'state',
          children: (el, s) => {
            const all = s.root?.canvasAllTickets
            if (all == null) return []
            return _computeGroups(s.root)
          }
        }
      },

      // ── RIGHT: Filters + detail ────────────────────────────────────────────
      RightPane: {
        width: '280px',
        minWidth: '220px',
        maxWidth: '320px',
        flexShrink: '0',
        flow: 'y',
        borderLeftWidth: 'V',
        borderLeftStyle: 'solid',
        borderLeftColor: 'line',
        overflow: 'auto',

        // Collapse below 1280px via CSS media query approach
        '@maxWidth1280': { display: 'none' },

        // Detail view for selected ticket
        TicketDetail: {
          hide: (el, s) => !s.root?.selectedTicket,
          flow: 'y',
          gap: 'V',

          DetailHeader: {
            flow: 'x',
            align: 'center space-between',
            padding: 'A A V A',
            borderBottomWidth: 'V',
            borderBottomStyle: 'solid',
            borderBottomColor: 'line',

            DetailTitle: {
              tag: 'h3',
              margin: '0',
              fontSize: 'Y1',
              fontWeight: '700',
              letterSpacing: '.1em',
              color: 'caption',
              text: 'SELECTED'
            },
            CloseDetail: {
              tag: 'button',
              type: 'button',
              padding: 'V Z',
              border: 'none',
              background: 'transparent',
              color: 'caption',
              cursor: 'pointer',
              fontSize: 'Y1',
              fontWeight: '700',
              text: '×',
              onClick: (e, el, s) => { s.root.update({ selectedTicket: null }) }
            }
          },

          DetailBody: {
            flow: 'y',
            gap: 'Z',
            padding: 'A',

            DetailId: {
              tag: 'span',
              fontSize: 'Y2',
              fontWeight: '700',
              color: 'caption',
              fontFamily: 'monospace',
              text: (el, s) => s.root?.selectedTicket?.external_id || `#${s.root?.selectedTicket?.id || ''}`
            },
            DetailTitleText: {
              tag: 'h4',
              margin: '0',
              fontSize: 'A',
              fontWeight: '600',
              lineHeight: '1.3',
              color: 'title',
              text: (el, s) => s.root?.selectedTicket?.title || ''
            },

            StatePill: {
              hide: (el, s) => !s.root?.selectedTicket?.state,
              tag: 'span',
              display: 'inline-flex',
              padding: 'V Z',
              round: 'Y2',
              fontSize: 'Y2',
              fontWeight: '700',
              color: (el, s) => TICKET_STATE_COLOR[s.root?.selectedTicket?.state] || 'caption',
              background: (el, s) => {
                const c = TICKET_STATE_COLOR[s.root?.selectedTicket?.state] || 'caption'
                return `color-mix(in srgb, var(--color-${c}) 16%, transparent)`
              },
              text: (el, s) => {
                const t = s.root?.selectedTicket
                return (t?.state || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
              }
            },

            PriorityPill: {
              hide: (el, s) => !s.root?.selectedTicket?.priority,
              tag: 'span',
              display: 'inline-flex',
              padding: 'V Z',
              round: 'Y2',
              fontSize: 'Y2',
              fontWeight: '700',
              color: (el, s) => PRIORITY_COLOR[s.root?.selectedTicket?.priority] || 'caption',
              background: (el, s) => {
                const c = PRIORITY_COLOR[s.root?.selectedTicket?.priority] || 'caption'
                return `color-mix(in srgb, var(--color-${c}) 16%, transparent)`
              },
              text: (el, s) => s.root?.selectedTicket?.priority || ''
            },

            AssigneeRow: {
              hide: (el, s) => !s.root?.selectedTicket?.assignee_email,
              flow: 'x',
              gap: 'V',
              align: 'center left',
              AvatarImg: {
                tag: 'img',
                width: 'Z1',
                height: 'Z1',
                round: '100%',
                src: (el, s) => {
                  const person = el.call('findPerson', s.root?.selectedTicket?.assignee_email)
                  return el.call('avatarUrl', person?.name || s.root?.selectedTicket?.assignee_email)
                }
              },
              AssigneeName: {
                tag: 'span',
                fontSize: 'Y2',
                color: 'caption',
                text: (el, s) => {
                  const person = el.call('findPerson', s.root?.selectedTicket?.assignee_email)
                  return person?.name || (s.root?.selectedTicket?.assignee_email || '').split('@')[0]
                }
              }
            },

            EpicRow: {
              hide: (el, s) => !s.root?.selectedTicket?.parentEpicTitle,
              flow: 'x',
              gap: 'V',
              align: 'center left',
              EpicChip: {
                tag: 'span',
                padding: 'V Z',
                round: 'Y2',
                fontSize: 'Y2',
                fontWeight: '600',
                background: 'cyan.15',
                color: 'cyan',
                text: (el, s) => s.root?.selectedTicket?.parentEpicTitle || ''
              }
            },

            StoryRow: {
              hide: (el, s) => !s.root?.selectedTicket?.parentStoryTitle,
              flow: 'x',
              gap: 'V',
              align: 'center left',
              StoryChip: {
                tag: 'span',
                padding: 'V Z',
                round: 'Y2',
                fontSize: 'Y2',
                fontWeight: '600',
                background: 'blue.15',
                color: 'blue',
                text: (el, s) => s.root?.selectedTicket?.parentStoryTitle || ''
              }
            },

            OpenDetailLink: {
              tag: 'button',
              type: 'button',
              marginTop: 'Z',
              align: 'center center',
              width: '100%',
              padding: 'Z',
              round: 'Y2',
              border: 'none',
              cursor: 'pointer',
              background: 'blue.15',
              color: 'blue',
              fontSize: 'Y2',
              fontWeight: '700',
              text: 'Open full detail',
              onClick: (e, el, s) => {
                // TicketDetailModal reads from s.root.selectedTicket
                // so just ensure it's set (it already is) and the modal
                // will open via its own show binding.
              }
            }
          }
        },

        // Placeholder when nothing is selected
        DetailEmpty: {
          hide: (el, s) => !!s.root?.selectedTicket,
          flow: 'y',
          align: 'center center',
          gap: 'A',
          padding: 'C A',
          textAlign: 'center',
          flex: '1',

          EmptyIcon: { extends: 'Icon', icon: 'ticket', boxSize: 'C', color: 'caption', opacity: '.25' },
          EmptyText: {
            tag: 'p',
            margin: '0',
            fontSize: 'Y1',
            color: 'caption',
            lineHeight: '1.5',
            text: 'Click a ticket card to see its details here.'
          }
        },

        // Priority filter section
        PriorityFilters: {
          flow: 'y',
          gap: 'V',
          padding: 'A',
          borderTopWidth: 'V',
          borderTopStyle: 'solid',
          borderTopColor: 'line',
          marginTop: 'auto',

          PriorityFilterLabel: {
            tag: 'span',
            fontSize: 'Y2',
            fontWeight: '700',
            letterSpacing: '.1em',
            color: 'caption',
            marginBottom: 'V',
            text: 'PRIORITY'
          },

          PriorityChips: {
            flow: 'x',
            gap: 'V',
            flexWrap: 'wrap',
            childExtends: 'CanvasFilterChip',
            childrenAs: 'state',
            children: (el, s) => {
              const active = s.root?.canvasPriorityFilter || null
              return [
                { key: null, label: 'All', filterKey: 'canvasPriorityFilter', active: !active },
                { key: 'P0', label: 'P0', filterKey: 'canvasPriorityFilter', active: active === 'P0' },
                { key: 'P1', label: 'P1', filterKey: 'canvasPriorityFilter', active: active === 'P1' },
                { key: 'P2', label: 'P2', filterKey: 'canvasPriorityFilter', active: active === 'P2' }
              ]
            }
          }
        }
      }
    }
  },

  // ── Toast overlay ────────────────────────────────────────────────────────────
  Toast: {
    hide: (el, s) => !s.root?.canvasToast,
    position: 'fixed',
    bottom: 'B',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: '99',
    padding: 'Z A',
    round: 'A',
    background: (el, s) => s.root?.canvasToast?.ok ? 'green.15' : 'red.15',
    borderWidth: 'V',
    borderStyle: 'solid',
    borderColor: (el, s) => s.root?.canvasToast?.ok ? 'green.a+30' : 'red.a+30',
    color: (el, s) => s.root?.canvasToast?.ok ? 'green' : 'red',
    fontSize: 'Y1',
    fontWeight: '600',
    pointerEvents: 'none',
    text: (el, s) => s.root?.canvasToast?.msg || ''
  }
}

// ── Epic rail item ────────────────────────────────────────────────────────────

export const CanvasEpicRailItem = {
  flow: 'y',
  gap: 'V',
  padding: 'Z A',
  cursor: 'pointer',
  transition: 'background 100ms ease',
  borderLeftWidth: '3px',
  borderLeftStyle: 'solid',
  borderLeftColor: (el, s) => {
    const active = el?.state?.root?.canvasEpicFilter
    return active === s.epicKey ? 'cyan' : 'transparent'
  },
  background: (el, s) => {
    const active = el?.state?.root?.canvasEpicFilter
    return active === s.epicKey ? 'cyan.06' : 'transparent'
  },
  ':hover': { background: 'overlay' },
  onClick: (e, el, s) => {
    const root = s.root || el.getRoot()
    const newKey = root?.canvasEpicFilter === s.epicKey ? null : s.epicKey
    root.update({ canvasEpicFilter: newKey })
    _syncUrl(root)
  },

  Row: {
    flow: 'x',
    gap: 'V',
    align: 'center space-between',

    EpicName: {
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: (el, s) => {
        const active = el?.state?.root?.canvasEpicFilter
        return active === el?.parent?.parent?.state?.epicKey ? '700' : '500'
      },
      color: (el, s) => {
        const active = el?.state?.root?.canvasEpicFilter
        return active === el?.parent?.parent?.state?.epicKey ? 'cyan' : 'title'
      },
      flex: '1',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => el.parent.parent.state.title || el.parent.parent.state.epicKey?.toUpperCase() || ''
    },

    DonePct: {
      tag: 'span',
      flexShrink: '0',
      fontSize: 'Y2',
      fontWeight: '600',
      color: (el, s) => (el.parent.parent.state.donePct || 0) >= 100 ? 'green' : 'caption',
      text: (el, s) => {
        const d = el.parent.parent.state.donePct || 0
        return d > 0 ? `${d}%` : ''
      }
    }
  },

  ProgBar: {
    hide: (el, s) => !(s.tasksTotal + s.bugsTotal),
    height: '3px',
    round: 'V',
    background: 'line',
    overflow: 'hidden',

    Fill: {
      height: '100%',
      round: 'V',
      background: (el, s) => {
        const d = el.parent.parent.state.donePct || 0
        return d >= 100 ? 'var(--color-green)' : 'var(--color-cyan)'
      },
      width: (el, s) => `${el.parent.parent.state.donePct || 0}%`,
      transition: 'width 0.2s ease'
    }
  }
}

// ── Board group ───────────────────────────────────────────────────────────────

export const CanvasBoardGroup = {
  flow: 'y',
  gap: 'A',

  // Group header — reuses StoriesSection header treatment for story grouping,
  // and EpicsView epic-row treatment for epic grouping.
  GroupHeader: {
    flow: 'x',
    gap: 'V',
    align: 'baseline left',
    paddingBottom: 'V',
    borderBottomWidth: 'V',
    borderBottomStyle: 'solid',
    borderBottomColor: 'line',

    AccentDot: {
      width: '10px',
      height: '10px',
      round: 'round',
      flexShrink: '0',
      alignSelf: 'center',
      background: (el, s) => `var(--color-${_groupAccent(_getGroupBy(el?.state?.root), s.groupKey)})`,
      boxShadow: (el, s) => {
        const c = _groupAccent(_getGroupBy(el?.state?.root), s.groupKey)
        return `0 0 10px color-mix(in srgb, var(--color-${c}) 55%, transparent)`
      }
    },

    GroupTitle: {
      tag: 'h2',
      margin: '0',
      fontSize: 'B',
      fontWeight: '700',
      letterSpacing: '-.02em',
      color: 'title',
      text: (el, s) => s.groupLabel || s.groupKey || '?'
    },

    TicketCount: {
      tag: 'span',
      fontSize: 'Y1',
      fontWeight: '600',
      color: 'caption',
      text: (el, s) => {
        const n = (s.tickets || []).length
        return `${n} ticket${n === 1 ? '' : 's'}`
      }
    },

    // Story meta (donePct) when grouping by story
    StoryMeta: {
      hide: (el, s) => _getGroupBy(el?.state?.root) !== 'story',
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      fontStyle: 'italic',
      text: (el, s) => {
        const storyId = s.groupKey
        const stories = el?.state?.root?.canvasStories || []
        const st = stories.find(x => String(x.id) === storyId)
        if (!st) return ''
        const ep = st.epicTitle || st.epicKey?.toUpperCase() || ''
        return ep ? `${ep}` : ''
      }
    }
  },

  // Ticket card grid — drop zone for drag-to-bucket.
  CardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: 'A',
    minHeight: 'B1',
    padding: 'Z',
    round: 'A',
    transition: 'background 120ms ease, border-color 120ms ease',
    borderWidth: 'V',
    borderStyle: 'dashed',
    borderColor: 'transparent',
    attr: {
      'data-drop-group': (el, s) => s.groupKey,
      'data-drag-over': ''
    },

    onDragover: (e, el, s) => {
      e.preventDefault()
      if (el.node) { el.node.dataset.dragOver = 'true' }
    },
    onDragleave: (e, el, s) => {
      if (el.node && !el.node.contains(e.relatedTarget)) {
        el.node.dataset.dragOver = ''
      }
    },
    onDrop: async (e, el, s) => {
      e.preventDefault()
      if (el.node) el.node.dataset.dragOver = ''
      const ticketId = parseInt(e.dataTransfer.getData('text/plain') || e.dataTransfer.getData('text/ticket-id'), 10)
      if (!ticketId) return
      const root = s.root || el.getRoot()
      const groupBy = _getGroupBy(root)
      const mutation = _dropMutation(groupBy, s.groupKey, root)
      if (!mutation) return

      // Optimistic update
      const prev = (root.canvasAllTickets || []).find(t => t.id === ticketId)
      if (!prev) return

      const nextTickets = (root.canvasAllTickets || []).map(t => {
        if (t.id !== ticketId) return t
        const updated = { ...t }
        if (groupBy === 'status') updated.state = mutation.value
        else if (groupBy === 'priority') updated.priority = mutation.value
        else if (groupBy === 'assignee') updated.assignee_email = mutation.value
        else if (groupBy === 'epic') {
          updated.parentEpicKey = s.groupKey === '_no_epic' ? '' : s.groupKey
          updated.parentEpicTitle = s.groupLabel || ''
        } else if (groupBy === 'story') {
          const stories = root.canvasStories || []
          const st = stories.find(x => String(x.id) === s.groupKey)
          updated.parentStoryId = st?.id || null
          updated.parentStoryTitle = st?.title || ''
          updated.parentStoryKey = st?.storyKey || ''
        }
        return updated
      })
      root.update({ canvasAllTickets: nextTickets })

      const ok = await patchTicketField(ticketId, mutation.field, mutation.value)
      _toast(root, ok ? 'Ticket moved.' : 'Move failed — reverting.', ok)
      if (!ok) {
        root.update({ canvasAllTickets: root.canvasAllTickets.map(t => t.id === ticketId ? prev : t) })
      }
    },

    // Empty-group drop placeholder
    DropPlaceholder: {
      hide: (el, s) => (s.tickets || []).length > 0,
      gridColumn: '1 / -1',
      padding: 'A',
      align: 'center center',
      round: 'A',
      borderWidth: 'V',
      borderStyle: 'dashed',
      borderColor: 'caption.3',
      color: 'caption',
      fontSize: 'Y2',
      fontStyle: 'italic',
      text: 'Drop tickets here'
    },

    childExtends: 'CanvasTicketCard',
    childrenAs: 'state',
    children: (el, s) => s.tickets || []
  }
}

// ── Ticket card for canvas board ───────────────────────────────────────────────
// Reuses the same visual language as TicketCard from MyViewMain.

export const CanvasTicketCard = {
  flow: 'y',
  gap: 'Z',
  padding: 'A',
  round: 'Z1',
  background: 'overlay',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  overflow: 'hidden',
  cursor: 'grab',
  transition: 'transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
  ':hover': {
    transform: 'translateY(-2px)',
    borderColor: 'overlayStrong',
    boxShadow: '0 8px 24px -8px var(--color-shadow)'
  },
  ':active': { cursor: 'grabbing' },

  // Selected visual highlight
  onRender: (el, s) => {
    if (!el.node) return
    const sel = el?.state?.root?.canvasSelected || []
    const isSelected = sel.includes(s.id)
    el.node.style.borderColor = isSelected ? 'var(--color-cyan)' : ''
    el.node.style.background = isSelected ? 'color-mix(in srgb, var(--color-cyan) 8%, var(--color-overlay))' : ''
  },

  attr: {
    draggable: 'true',
    'data-ticket-id': (el, s) => String(s.id || '')
  },

  onDragstart: (e, el, s) => {
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', String(s.id))
    e.dataTransfer.setData('text/ticket-id', String(s.id))
    if (s.root) s.root.update({ _canvasDragging: s.id })
  },
  onDragend: (e, el, s) => {
    if (s.root) s.root.update({ _canvasDragging: null })
    document.querySelectorAll('[data-drop-group]').forEach(el => {
      el.dataset.dragOver = ''
    })
  },

  // Click: select (cmd/shift for multi), else open detail
  onClick: (e, el, s) => {
    const root = s.root || el.getRoot()
    if (!root) return
    if (e.metaKey || e.ctrlKey) {
      // Toggle individual selection
      const sel = root.canvasSelected || []
      const next = sel.includes(s.id) ? sel.filter(x => x !== s.id) : [...sel, s.id]
      root.update({ canvasSelected: next })
    } else if (e.shiftKey) {
      // Range select — add to selection without replacing
      const sel = root.canvasSelected || []
      if (!sel.includes(s.id)) root.update({ canvasSelected: [...sel, s.id] })
    } else {
      root.update({ selectedTicket: { ...s }, canvasSelected: [] })
    }
  },

  // Keyboard: arrow navigation handled by CanvasBoardGroup
  onKeydown: (e, el, s) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      const root = s.root || el.getRoot()
      if (root) root.update({ selectedTicket: { ...s } })
    }
  },

  tabindex: '0',
  role: 'button',

  // Tag row — issue type + epic + state pills
  TagRow: {
    display: (el, s) => (s.type || s.parentEpicTitle || s.state) ? 'flex' : 'none',
    flow: 'x',
    align: 'center left',
    gap: 'Y',
    flexWrap: 'wrap',

    TypePill: {
      display: (el, s) => s.type ? 'inline-flex' : 'none',
      tag: 'span',
      align: 'center center',
      padding: 'V Z',
      round: 'C',
      fontSize: 'V',
      fontWeight: '700',
      letterSpacing: '.06em',
      color: (el, s) => s.type === 'bug' ? 'red' : 'blue',
      background: (el, s) => s.type === 'bug'
        ? 'color-mix(in srgb, var(--color-red) 14%, transparent)'
        : 'color-mix(in srgb, var(--color-blue) 14%, transparent)',
      text: (el, s) => (s.type || '').toUpperCase()
    },

    StatePill: {
      display: (el, s) => s.state ? 'inline-flex' : 'none',
      tag: 'span',
      align: 'center center',
      padding: 'V Z',
      round: 'C',
      fontSize: 'V',
      fontWeight: '700',
      letterSpacing: '.06em',
      color: (el, s) => TICKET_STATE_COLOR[s.state] || 'caption',
      background: (el, s) => {
        const c = TICKET_STATE_COLOR[s.state] || 'caption'
        return `color-mix(in srgb, var(--color-${c}) 14%, transparent)`
      },
      text: (el, s) => (s.state || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },

    PriorityDot: {
      display: (el, s) => s.priority ? 'inline-flex' : 'none',
      title: (el, s) => `Priority: ${s.priority || ''}`,
      width: 'Z',
      height: 'Z',
      round: '50%',
      marginLeft: 'auto',
      alignSelf: 'center',
      background: (el, s) => PRIORITY_COLOR[s.priority] || 'caption',
      boxShadow: (el, s) => `0 0 6px color-mix(in srgb, var(--color-${PRIORITY_COLOR[s.priority] || 'caption'}) 40%, transparent)`
    }
  },

  // Title
  CardTitle: {
    tag: 'h3',
    fontSize: 'Y2',
    fontWeight: '600',
    letterSpacing: '-.01em',
    color: 'title',
    margin: '0',
    lineHeight: '1.35',
    style: {
      display: '-webkit-box',
      overflow: 'hidden',
      '-webkit-line-clamp': '2',
      '-webkit-box-orient': 'vertical'
    },
    text: (el, s) => `#${s.external_id || s.id} · ${s.title || ''}`
  },

  // Footer — assignee + age
  CardFooter: {
    flow: 'x',
    gap: 'V',
    align: 'center space-between',
    marginTop: 'V',

    Assignee: {
      hide: (el, s) => !s.assignee_email,
      flow: 'x',
      align: 'center left',
      gap: 'V',
      Avi: {
        tag: 'img',
        width: 'Z1',
        height: 'Z1',
        round: '100%',
        src: (el, s) => {
          const person = el.call('findPerson', el.parent.parent.state.assignee_email)
          return el.call('avatarUrl', person?.name || el.parent.parent.state.assignee_email)
        }
      },
      AviName: {
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        text: (el, s) => {
          const person = el.call('findPerson', el.parent.parent.state.assignee_email)
          return (person?.name || el.parent.parent.state.assignee_email || '').split(' ')[0].split('@')[0]
        }
      }
    },

    Age: {
      hide: (el, s) => !s.updated_at,
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) => s.updated_at ? s.updated_at.slice(0, 10) : ''
    }
  }
}

// ── Small filter chip ─────────────────────────────────────────────────────────

export const CanvasFilterChip = {
  tag: 'button',
  type: 'button',
  padding: 'V Z',
  round: 'Y2',
  fontSize: 'Y2',
  fontWeight: '600',
  cursor: 'pointer',
  borderWidth: 'V',
  borderStyle: 'solid',
  borderColor: (el, s) => s.active ? 'cyan' : 'line',
  background: (el, s) => s.active ? 'cyan.a+10' : 'transparent',
  color: (el, s) => s.active ? 'cyan' : 'caption',
  transition: 'all 0.12s ease',
  text: (el, s) => s.label || s.key,
  onClick: (e, el, s) => {
    const root = s.root || el.getRoot()
    if (root && typeof root.update === 'function') {
      const filterKey = s.filterKey || 'canvasStateFilter'
      root.update({ [filterKey]: s.key })
    }
  }
}

// ── Depth tab (legacy graph depth selector, kept for compat) ──────────────────

export const CanvasDepthTab = {
  tag: 'a',
  padding: 'V Z',
  round: 'Y2',
  fontSize: 'Y2',
  fontWeight: '600',
  textDecoration: 'none',
  cursor: 'pointer',
  borderWidth: 'V',
  borderStyle: 'solid',
  borderColor: (el, s) => s.active ? 'cyan' : 'line',
  background: (el, s) => s.active ? 'cyan.a+10' : 'transparent',
  color: (el, s) => s.active ? 'cyan' : 'caption',
  transition: 'all 0.12s ease',
  href: (el, s) => `/tickets/canvas?depth=${encodeURIComponent(s.key || 'full')}`,
  text: (el, s) => s.label || s.key
}
